import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, forkJoin } from 'rxjs';

import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { PermissionEnum, ProductDto, ProductPackageDto, SdkCategoryDto, SdkDto, SdkVendorDto } from '../../dto';
import { HttpProductService } from '../../services/product/http-product.service';
import { HttpSdkService } from '../../services/sdk/http-sdk.service';
import { HttpPackageService } from '../../services/package/http-package.service';
import { SpinnerService } from '../spinner/spinner.service';
import { UnauthorizedAccessEditWarningComponent } from '../unauthorized-access-edit-warning/unauthorized-access-edit-warning.component';
import { AuthService } from '../../services/auth/auth.service';


@Component({
    selector: 'app-product-packages-modal',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NzModalModule,
        NzButtonModule,
        NzTableModule,
        NzFormModule,
        NzInputModule,
        NzSelectModule,
        NzIconModule,
        NzTagModule,
        UnauthorizedAccessEditWarningComponent,
        NzDividerModule
    ],
    templateUrl: './product-packages-modal.component.html',
    styleUrls: ['./product-packages-modal.component.scss']
})
export class ProductPackagesModalComponent {
    @Input() modalId = 'productPackagesModal';
    
    isVisible = false;
    isLoadingPackages = false;
    isLoadingData = false;
    isSaving = false;

    product?: ProductDto;
    packages: ProductPackageDto[] = [];
    availableSdks: SdkDto[] = [];
    
    filteredSdks: SdkDto[] = [];
    categories?: SdkCategoryDto[];
    vendors?: SdkVendorDto[];
    categoryFilter: number | null = null;
    vendorFilter: number | null = null;

    form!: FormGroup;
    editingPackageId?: number;
    showForm = false;

    constructor(
        private fb: FormBuilder,
        private toastr: ToastrService,
        private productService: HttpProductService,
        private packageService: HttpPackageService,
        private sdkService: HttpSdkService,
        private authService: AuthService
    ) {
        this.form = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
            description: ['', [Validators.maxLength(2000)]],
            sdkIds: [[], Validators.required] // Array of SDK IDs
        });
    }

    get canEdit(): boolean {
        return this.authService.hasAccess(PermissionEnum.PackagesEdit);
    }
    get canAdd(): boolean {
        return this.authService.hasAccess(PermissionEnum.PackagesAdd);
    }

    open(product: ProductDto) {
        this.product = product;
        this.showForm = false;
        this.isVisible = true;
        this.resetFilters();
        this.loadSdks();
        this.loadPackages();
    }

    handleCancel() {
        this.isVisible = false;
        this.resetFilters();
    }
    cancelEdit() {
        this.showForm = false;
        this.resetFilters();
    }

    loadSdks() {
        // Fetch all active SDKs to populate the dropdown
        this.sdkService.getAllSdks({}).subscribe({
            next: (data) => {
                this.availableSdks = data;
                this.fetchData();
                this.applyFilters();

            },
            error: () => this.toastr.error('Failed to load available SDKs')
        });
    }

    loadPackages() {
        if (!this.product?.id) return;
        this.isLoadingPackages = true;
        
        // You'll need to create this method in your HttpProductService
        this.packageService.getPackages(this.product.id)
            .pipe(finalize(() => this.isLoadingPackages = false))
            .subscribe({
                next: (data) => this.packages = data,
                error: (err) => this.toastr.error('Failed to load packages')
            });
    }

    fetchData() {
        this.isLoadingData = true; 
        forkJoin({
            categories: this.sdkService.getSdkCategories(),
            vendors: this.sdkService.getSdkVendors()
        })
        .pipe(
            catchError(error => {
                console.error('Error fetching SDK reference data', error);
                this.toastr.error('Failed to fetch form data: ' + (error.error?.error || error.message));
                throw error;
            }),
            finalize(() => this.isLoadingData = false) 
        )
        .subscribe(data => {
            this.categories = data.categories;
            this.vendors = data.vendors;
        });
    }

    onFilterChange() {
        this.applyFilters();
    }

    applyFilters() {
        this.filteredSdks = this.availableSdks.filter(sdk => {
            const matchCategory = this.categoryFilter ? sdk.category?.id === this.categoryFilter : true;
            const matchVendor = this.vendorFilter ? sdk.vendor?.id === this.vendorFilter : true;
            return matchCategory && matchVendor;
        });
    }

    // Custom search function for the nz-select text input
    customSearchFn = (input: string, option: any): boolean => {
        const id = option.nzValue;
        const sdk = this.availableSdks.find(s => s.id === id);
        if (!sdk) return false;
        
        const searchTerm = input.toLowerCase();
        return (sdk.name || '').toLowerCase().includes(searchTerm) ||
               (sdk.category?.name || '').toLowerCase().includes(searchTerm) ||
               (sdk.vendor?.name || '').toLowerCase().includes(searchTerm);
    };

    addNewPackage() {
        this.editingPackageId = undefined;
        this.form.reset({ sdkIds: [] });
        this.resetFilters();
        this.showForm = true;
    }

    editPackage(pkg: ProductPackageDto) {
        this.editingPackageId = pkg.id;
        this.form.patchValue({
            name: pkg.name,
            description: pkg.description,
            sdkIds: pkg.sdks?.map(s => s.id) || []
        });
        this.resetFilters();
        this.showForm = true;
    }

    resetFilters() {
        this.categoryFilter = null;
        this.vendorFilter = null;
        this.applyFilters();
    }

    savePackage() {
        if (this.form.invalid || !this.product?.id) {
            Object.values(this.form.controls).forEach(c => { c.markAsDirty(); c.updateValueAndValidity(); });
            return;
        }

        this.isSaving = true;
        var sdkIds: number[] = this.form.value.sdkIds;
        var sdks: SdkDto[] = [];
        sdkIds.forEach(sdkId => {
            sdks.push({id: sdkId});
        });
        const payload: ProductPackageDto = {
            id: this.editingPackageId,
            product: {id: this.product.id},
            name: this.form.value.name,
            description: this.form.value.description,
            sdks: sdks
        };

        // You'll need to create this method in your HttpProductService
        this.packageService.savePackage(payload)
            .pipe(finalize(() => this.isSaving = false))
            .subscribe({
                next: (res) => {
                    if (res.success) {
                        this.toastr.success('Package saved successfully');
                        this.showForm = false;
                        this.loadPackages(); // Reload the table
                    } else {
                        this.toastr.error(res.error || 'Failed to save package');
                    }
                },
                error: (err) => this.toastr.error(err.error?.error || 'Error saving package')
            });
    }
}